/**
 * localization-suggest.js — <localization-suggest>, the first-visit "Are you in the right place?" prompt.
 *
 * The markup is a closed <dialog> holding Shopify's localization form (snippets/localization-suggest.liquid).
 * This opens it only when Shopify's own endpoint, `browsing_context_suggestions.json`, proposes a
 * country or language other than the current one, with the proposal pre-selected. It never moves the
 * visitor: Save location posts the form as it is, and dismissing keeps them where they are.
 *
 * It waits for the cookie banner. A modal <dialog> sits in the top layer and would hide the banner,
 * so when Cookiebot is present and the visitor has not answered, the dialog opens only after
 * `CookiebotOnConsentReady` (or an accept or decline). With no Cookiebot there is no banner to
 * collide with, and it opens at once.
 *
 * Either answer is remembered in localStorage, so the question is asked once. Every failure is
 * silent and leaves the page as rendered: no storage, a failed request or an empty suggestion all
 * mean no dialog. The endpoint answers with no suggestions until a market for the visitor's
 * country is active, which is why nothing shows today for a Swedish visitor.
 */
(() => {
  if (customElements.get('localization-suggest')) return;

  const KEY = 'valeryd-location-prompt';
  const answered = () => {
    try {
      return !!localStorage.getItem(KEY);
    } catch (e) {
      return false;
    }
  };
  const remember = () => {
    try {
      localStorage.setItem(KEY, '1');
    } catch (e) {
      // No storage: the question is simply asked again next visit.
    }
  };

  // Resolves when there is no cookie banner to cover: no Cookiebot, or the visitor has answered it.
  // Cookiebot fires these events on every page load once the stored answer is known, and again when
  // the visitor answers, so a returning visitor is not held up.
  const afterConsent = () =>
    new Promise((resolve) => {
      const cookiebot = window.Cookiebot;
      if (!cookiebot || cookiebot.hasResponse) return resolve();
      const events = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'];
      const done = () => {
        events.forEach((e) => window.removeEventListener(e, done));
        resolve();
      };
      events.forEach((e) => window.addEventListener(e, done));
    });

  class LocalizationSuggest extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || this.dialog.open) return; // rendered open: the styleguide
      this.dialog.addEventListener('close', remember);
      const form = this.dialog.querySelector('form');
      if (form) form.addEventListener('submit', remember);
      const dismiss = this.dialog.querySelector('[data-suggest-dismiss]');
      if (dismiss) dismiss.addEventListener('click', () => this.dialog.close());
      if (!answered()) this.load();
    }

    async load() {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const query = new URLSearchParams({ 'country[enabled]': 'true', 'language[enabled]': 'true' });
      if (this.dataset.country) query.set('country[exclude]', this.dataset.country);
      if (this.dataset.language) query.set('language[exclude]', this.dataset.language);

      let data;
      try {
        const res = await fetch(root + 'browsing_context_suggestions.json?' + query);
        if (!res.ok) return;
        data = await res.json();
      } catch (e) {
        return;
      }

      const first = data && data.suggestions && data.suggestions[0];
      const parts = first && first.parts;
      if (!parts) return;

      // A proposal counts only if the form offers it; one it does not offer cannot be saved.
      const choose = (field, part) => {
        const select = this.querySelector('select[name="' + field + '"]');
        const code = part && part.handle;
        if (!select || !code) return false;
        const offered = Array.prototype.some.call(select.options, (o) => o.value === code);
        if (offered) {
          select.value = code;
          // Lets <country-select> show the proposed country's flag.
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return offered;
      };
      const country = choose('country_code', parts.country);
      const language = choose('language_code', parts.language);
      if (!country && !language) return;
      await afterConsent();
      if (!this.dialog.open && !answered()) this.dialog.showModal();
    }
  }

  customElements.define('localization-suggest', LocalizationSuggest);
})();
